// /src/pages/HomePage.jsx

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Carrousel from '../components/tools/Carrousel';
import DivisionBarChart from '../components/tools/charts/DivisionBarChart';
import worldmap from '../medias/home/map.png';

function Home() {
  const chartRefs = useRef({
    divisionBarChartRef: null,
    regionDonutChartRef: null,
    fleetBarChartRef: null,
  });

  const documentName = 'welcome-bloc';
  const STYLE_ID = `style-${documentName}`;

  const [welcomeHtml, setWelcomeHtml] = useState('');
  const [hash, setHash] = useState(null);

  // fetch public HTML+CSS once
  useEffect(() => {
    const fetchRteContents = async () => {
      try {
        const resp = await axios.get('/api/admin/lexical/retrieve-public-html-document', {
          params: { documentName }
        });
        const { htmlContent, cssPublic, contentHash } = resp.data?.data || {};

        // inject/update a single <style> per document, only if hash changed
        let styleEl = document.getElementById(STYLE_ID);
        const prevHash = styleEl?.getAttribute('data-hash') || null;

        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = STYLE_ID;

          // set CSP nonce BEFORE appending (server injects <meta name="csp-nonce" ...>)
          const meta = document.querySelector('meta[name="csp-nonce"]');
          const nonce = meta && meta.content;
          if (nonce) styleEl.setAttribute('nonce', nonce);

          document.head.appendChild(styleEl);
        }

        if (contentHash && contentHash !== prevHash) {
          styleEl.textContent = cssPublic || '';
          styleEl.setAttribute('data-hash', contentHash);
          setHash(contentHash);
        }

        setWelcomeHtml(htmlContent || '');
      } catch (error) {
        console.error('Error fetching public HTML+CSS:', error);
        setWelcomeHtml('');
      }
    };
    fetchRteContents();
  }, []);

  // use class toggle instead of inline style (CSP: style-src-attr 'none')
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('anim-running');
          }
        });
      },
      { threshold: 0.5 }
    );

    Object.values(chartRefs.current).forEach(ref => { if (ref) observer.observe(ref); });
    return () => Object.values(chartRefs.current).forEach(ref => { if (ref) observer.unobserve(ref); });
  }, []);

  // Trusted Types passthrough
  const trustedHTML = (() => {
    if (window.trustedTypes) {
      try {
        const policy = window.trustedTypes.createPolicy('pallasPages', {
          createHTML: x => x, createScript: x => x, createScriptURL: x => x
        });
        return policy.createHTML(welcomeHtml);
      } catch {
        return window.trustedTypes.createPolicy('pallasPages').createHTML(welcomeHtml);
      }
    }
    return welcomeHtml;
  })();

  return (
    <div className="home-container">
      <div className="carousel-with-title">
        <Carrousel />
      </div>

      <div className="carousel-bottom"></div>

      <div className="org-statistics margin-top-spacer-60">
        <div className="content-container">
          <div className="banner glass-card">
            {/* container must match namespace selector */}
            <div
              className="homepage welcome-text lexical-rendered-content"
              data-doc={documentName}
              role="document"
              dangerouslySetInnerHTML={{ __html: trustedHTML }}
            />
          </div>

          <h2 className="homepage catchPhrase">respect - responsibility - courage</h2>

          <div className="chart-row">
            <div className="member-count-display">
              <div
                className="bar-chart divisions glass-card"
                ref={el => (chartRefs.current.divisionBarChartRef = el)}
              >
                <h3 className="chart-title">Division balance</h3>
                <DivisionBarChart />
              </div>

              <div
                className="pie-chart regions glass-card worldmap-container"
                ref={el => (chartRefs.current.regionDonutChartRef = el)}
              >
                <img className="worldmap" src={worldmap} alt="World map" />
              </div>
            </div>
          </div>

          {/* optional debug */}
          {/* <small className="hash-debug">hash: {hash}</small> */}
        </div>
      </div>
    </div>
  );
}

export default Home;